import { useState } from "react";

interface FAQItem {
  question: string;
  answer: string;
  category: "compliance" | "territory" | "onboarding" | "platform";
}

const FAQS: FAQItem[] = [
  {
    category: "compliance",
    question: "Is Imperium TCPA compliant? How do you handle calling regulations?",
    answer:
      "Yes — TCPA compliance is built into the core calling logic, not bolted on as an afterthought. Imperium uses TCPA-aware dialing rules, automatic do-not-call scrubbing, opt-out detection, and consent management across every campaign. Our SOC 2 Type II certified infrastructure ensures every call is placed within legal guardrails. We continuously monitor regulatory updates to keep your outreach defensible and compliant.",
  },
  {
    category: "compliance",
    question: "What is SOC 2 Type II and why does it matter for title companies?",
    answer:
      "SOC 2 Type II is one of the highest independently audited security certifications in the software industry. It verifies that our platform maintains rigorous data security, availability, and confidentiality controls — not just at a point in time, but continuously over a period. For title companies handling sensitive transaction data, client information, and agent relationships, this means your data (and your clients' data) is protected to an enterprise standard.",
  },
  {
    category: "compliance",
    question: "How does opt-out and consent management work?",
    answer:
      "Imperium automatically detects and honors opt-out requests in real time. If a contact requests removal during a call, they are immediately flagged and suppressed from all future outreach — with no manual list management required. Consent preferences are stored, tracked, and surfaced in your reporting dashboard. You maintain full audit trail access for every contact interaction.",
  },
  {
    category: "territory",
    question: "How does territory locking work?",
    answer:
      "Imperium operates on a statewide territory model. When you claim a state — either exclusively or as a shared territory — that state is assigned to your account. Exclusive territory means you are the only Imperium client whose calls fire in that state. Shared territory allows up to 3 non-competing title companies to operate in the same state at a lower price point. Once all available slots in a state are claimed, it is fully locked and no new clients can enter.",
  },
  {
    category: "territory",
    question: "What is the difference between exclusive and shared territory?",
    answer:
      "Exclusive territory gives you 100% of the triggered opportunities in your state — every MLS listing, every pending closing, every pre-approval activity routes calls to you and only you. Shared territory allows up to 3 non-competing title companies to operate in the same state. Because we carefully vet that shared clients do not compete directly with each other, you still get strong coverage — just at a reduced cost. Most title companies in high-activity states opt for exclusive.",
  },
  {
    category: "territory",
    question: "What happens if my state is listed as 'Limited'?",
    answer:
      "Limited means one or more territory slots are still available, but they are filling fast. You can still secure an exclusive or shared position before it closes. We recommend checking availability as soon as possible — once a state is fully claimed, there is no waitlist. Availability is updated daily.",
  },
  {
    category: "territory",
    question: "Can I expand to multiple states?",
    answer:
      "Absolutely. Many Imperium clients operate title companies across multiple states and hold territory in each. You can claim exclusive or shared territory in as many states as your operation covers. Multi-state pricing is available — reach out through the territory application to discuss statewide or regional packages.",
  },
  {
    category: "onboarding",
    question: "What happens after I sign up?",
    answer:
      "Once you apply and your territory is confirmed, you'll be paired with a dedicated Imperium success manager who walks you through the full onboarding process. This includes connecting your contact database (for the Outbound Engine plan), configuring your calling script and voice, setting up lead routing to your closers, and running a test call before going live. Most clients are fully operational within 48–72 hours of signing.",
  },
  {
    category: "onboarding",
    question: "How long does it take to go live?",
    answer:
      "For the Full Market Control plan, which uses real-time MLS and transaction triggers, most clients are live within 48–72 hours. For the Outbound Engine plan using your existing contact database, it depends on the size of your list and any custom script configuration — typically 2–5 business days. Your dedicated success manager manages the entire setup process so you don't need to touch any technical configuration.",
  },
  {
    category: "onboarding",
    question: "Do I need to provide my own contact list?",
    answer:
      "It depends on the plan. The Outbound Engine plan uses your existing agent and loan officer database to run consistent relationship campaigns. The Full Market Control plan sources contacts through real-time MLS listing and transaction data — so no existing list is required. Many clients use both plans together: Full Market Control for new closing-triggered outreach plus Outbound Engine to nurture their existing network.",
  },
  {
    category: "platform",
    question: "What does the AI actually say on the call?",
    answer:
      "The AI voice is trained to represent your title company in a natural, professional, non-robotic tone. Scripts are customized during onboarding to reflect your company name, your positioning, and the type of relationship you want to build. A typical call opens with a soft, relevant introduction — referencing the specific listing or transaction activity that triggered the call — and transitions into a brief positioning statement that opens the door for a follow-up. You can hear real examples in the demo call section above.",
  },
  {
    category: "platform",
    question: "What happens when someone is interested after a call?",
    answer:
      "Interested leads are flagged in real time and immediately routed to your designated closer — via SMS alert, email notification, or CRM integration depending on your setup. The AI can also schedule a callback or transfer the call live if you choose. No lead sits in a queue waiting to be processed manually. Speed-to-follow-up is critical in title, and Imperium is built to eliminate that delay entirely.",
  },
];

const CATEGORIES = [
  { key: "all", label: "All Questions" },
  { key: "compliance", label: "TCPA & Compliance" },
  { key: "territory", label: "Territory" },
  { key: "onboarding", label: "Getting Started" },
  { key: "platform", label: "Platform" },
];

export default function FAQSection() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const filtered =
    activeCategory === "all"
      ? FAQS
      : FAQS.filter((f) => f.category === activeCategory);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="bg-alt-bg py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-questionnaire-line text-navy text-xs" />
            <span className="text-xs font-semibold text-navy">Common Questions</span>
          </div>
          <h2
            className="text-navy font-semibold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            Questions We Hear All the Time
          </h2>
          <p className="text-[#4a5568] text-base" style={{ lineHeight: "1.65" }}>
            Straight answers on compliance, territory, and how Imperium works from day one.
          </p>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => { setActiveCategory(cat.key); setOpenIndex(null); }}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key
                  ? "bg-navy text-white"
                  : "bg-white border border-gray-200 text-[#4a5568] hover:border-gray-300"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {filtered.map((faq, i) => {
            const isOpen = openIndex === i;
            const categoryColors: Record<string, string> = {
              compliance: "text-green-600 bg-green-50 border-green-200",
              territory: "text-secondary bg-blue-50 border-blue-200",
              onboarding: "text-primary bg-orange-50 border-orange-200",
              platform: "text-navy bg-gray-100 border-gray-200",
            };
            const categoryLabels: Record<string, string> = {
              compliance: "Compliance",
              territory: "Territory",
              onboarding: "Getting Started",
              platform: "Platform",
            };

            return (
              <div
                key={i}
                className={`bg-white rounded-2xl border transition-all overflow-hidden ${
                  isOpen ? "border-navy/20" : "border-gray-100 hover:border-gray-200"
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-start gap-3 p-5 sm:p-6 text-left cursor-pointer"
                >
                  {/* Category chip — hidden on mobile (tabs above already filter) */}
                  <span
                    className={`hidden sm:inline-flex text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border flex-shrink-0 mt-0.5 ${categoryColors[faq.category]}`}
                  >
                    {categoryLabels[faq.category]}
                  </span>

                  <span className="flex-1 text-navy font-semibold text-sm leading-snug pr-2">
                    {faq.question}
                  </span>

                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0 transition-colors mt-0.5 ${
                      isOpen ? "bg-navy text-white" : "bg-gray-100 text-[#4a5568]"
                    }`}
                  >
                    <i
                      className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>

                {isOpen && (
                  <div className="px-6 pb-6">
                    <div className="md:ml-[calc(theme(spacing.4)+3rem)] border-t border-gray-100 pt-4">
                      <p className="text-[#4a5568] text-sm leading-relaxed" style={{ lineHeight: "1.75" }}>
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center bg-white rounded-2xl p-8 border border-gray-100">
          <div className="w-12 h-12 flex items-center justify-center bg-orange-50 rounded-xl mx-auto mb-4">
            <i className="ri-customer-service-2-line text-primary text-xl" />
          </div>
          <h3 className="text-navy font-semibold text-base mb-2">Still have questions?</h3>
          <p className="text-[#4a5568] text-sm mb-6" style={{ lineHeight: "1.65" }}>
            Our team is happy to walk you through the platform, answer compliance questions,
            or check what territory is available in your state right now.
          </p>
          <a
            href="#territory"
            className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            Talk to the Imperium Team
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
